import { useQueryClient } from '@tanstack/react-query';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { cancelCartQuery, getCartCache, updateCart } from '@telialt/core-eshop/src/helpers/cartQueryUtils';
import {
    resetToPreviousCartState,
    useMutationWithCartRefresh,
} from '@telialt/core-eshop/src/hooks/mutations/useMutationWithCartRefresh';
import { useCartQuery } from '@telialt/core-eshop/src/hooks/queries/useCartQuery';
import { ESHOP_MUTATION_KEY } from '@telialt/core-eshop/src/model';
import { apiUpdateInitialPaymentAmount } from '@telialt/core-eshop/src/store/api';
import { InitialPaymentOption } from '@telialt/models/src/EshopModel';
import { useLogErrorEshop } from '@telialt/utils/src/datadog/useLogError';
import { useSegment } from '@telialt/utils/src/segment';

export const useSelectInitialPaymentOptionMutation = () => {
    const { t } = useTranslations();
    const segment = useSegment();
    const queryClient = useQueryClient();
    const { cartId } = useCartQuery();

    const { mutate, isPending, error } = useMutationWithCartRefresh(
        async (initialPaymentOption: InitialPaymentOption) =>
            (await apiUpdateInitialPaymentAmount(segment, { cartId }, initialPaymentOption.initialPaymentPrice.value))
                .data,
        {
            mutationKey: [ESHOP_MUTATION_KEY.UPDATE_INITIAL_PAYMENT_OPTION],
            onMutate: async (initialPaymentOption: InitialPaymentOption) => {
                await cancelCartQuery(queryClient);
                const cartCache = getCartCache(queryClient);
                updateCart(queryClient, {
                    ...cartCache,
                    selectedInitialPaymentOption: initialPaymentOption,
                    selectedInitialPayment: initialPaymentOption.initialPaymentPrice.value,
                });

                return cartCache;
            },
            onError: resetToPreviousCartState(queryClient, t['eshop.product_page.action_error']),
        },
    );
    useLogErrorEshop(error);

    return {
        selectInitialPaymentOption: mutate,
        isLoadingSelectInitialPaymentOption: isPending,
        errorSelectInitialPaymentOption: error,
    };
};
